import { Field, Form, Formik } from "formik";
import * as React from "react";
import { useParams } from "react-router";
import { v4 as uuidv4 } from "uuid";
import * as Yup from "yup";
import { t } from "../../../i18n/util";
import { API } from "../../../network/API";
import { generalStore } from "../../../stores/GeneralStore";
import { THEME, isDal } from "../../../theme/ThemeStore";
import { ButtonContainer } from "../../ui/ButtonContainer";
import { ContentWrapper } from "../../ui/ContentWrapper";
import { CustomInputField } from "../../ui/CustomInputField";
import { ErrorMessagesList, HrBotButton, HrBotErrorMessage, ImageLogo } from "../../ui/Primitives";
import { UploadField } from "../../ui/UploadField";
import { ApplicantIdParam, CodingIdParam, fileErrorToString, megabytes } from "../../util/helpers";
import { FileError } from "../DataUploadForm";

export const CodeExampleSubmissionSite = () => {
    const { applicantId } = useParams<ApplicantIdParam>();
    const { codingId } = useParams<CodingIdParam>();

    const [submitted, setSubmitted] = React.useState<undefined | boolean>(undefined);

    React.useEffect(() => {
        const getStatus = async () => {
            try {
                const res = await API.getCodingSubmissionStatus(applicantId, codingId);
                if (res.submitted) {
                    setSubmitted(true);
                } else {
                    setSubmitted(false);
                }
            } catch (error) {
                generalStore.setError(t("error.loadCodingSubmissionStatus"), error);
            }
        };

        getStatus();
    }, [applicantId, codingId]);

    const handleSubmit = async (model: { answer: string; file?: File }) => {
        try {
            generalStore.isLoading = true;

            if (!model.answer || !model.file) {
                throw new Error(t("error.submitCodingExample"));
            }

            await API.postCodingSubmission(applicantId, codingId, {
                comments: model.answer,
                archiveFile: model.file
            });
            setSubmitted(true);
        } catch (error) {
            generalStore.setError(t("error.submitCodingExample"), error);
        } finally {
            generalStore.isLoading = false;
        }
    };

    const [fileErrors, setFileErrors] = React.useState<FileError[]>([]);

    return (
        <div style={{ padding: "29px 18px" }}>
            <ImageLogo style={{ width: "auto", height: 37 }} />
            <ContentWrapper>
                {submitted === true && (
                    <React.Fragment>
                        <h2
                            style={{
                                color: THEME.customColors.body1Dark,
                                fontWeight: 400,
                                fontSize: 28,
                                marginBottom: 42
                            }}
                        >
                            {t("screen.codeExample.dearApplicant")}
                        </h2>
                        <p>{t("screen.codeExample.submitted1")}</p>
                        <br />
                        <p>{t("screen.codeExample.submitted2")}</p>
                    </React.Fragment>
                )}
                {submitted === false && (
                    <React.Fragment>
                        <h2
                            style={{
                                color: THEME.customColors.body1Dark,
                                fontWeight: 400,
                                fontSize: 28,
                                marginBottom: 42
                            }}
                        >
                            {t("screen.codeExample.dearApplicant")}
                        </h2>
                        <p>{t("screen.codeExample.instructions1")}</p>
                        <br />
                        <p>{t("screen.codeExample.instructions2")}</p>
                        <br />
                        <p style={{ marginBottom: 32 }}>{t("screen.codeExample.instructions3")}</p>
                        <Formik
                            initialValues={{ answer: "", file: undefined }}
                            onSubmit={handleSubmit}
                            validationSchema={Yup.object().shape({
                                answer: Yup.string().required(t("error.required")),
                                file: Yup.mixed().required(t("error.missingFile"))
                            })}
                            validateOnChange
                            validateOnBlur
                        >
                            {({ errors, touched, values, setFieldValue }) => (
                                <Form>
                                    <div
                                        style={{
                                            display: "flex",
                                            flexDirection: "column",
                                            height: "100%",
                                            gap: 16
                                        }}
                                    >
                                        <Field
                                            component={CustomInputField}
                                            label={t("screen.codeExample.yourAnswer")}
                                            name="answer"
                                            type="text"
                                            multiline
                                            rows={18}
                                            errorMessage={errors.answer}
                                            isTouched={touched.answer}
                                            required
                                        />

                                        <UploadField
                                            name={t("screen.dataUpload.dialog.radio.file")}
                                            type="application/zip"
                                            value={values.file}
                                            sm={12}
                                            fileSize={megabytes(100)}
                                            onUploadFile={(file) => {
                                                setFieldValue("file", file);
                                            }}
                                            onDelete={() => {
                                                setFieldValue("file", null);
                                            }}
                                            onSetFileErrors={(newFileErrors) => {
                                                setFileErrors(newFileErrors);
                                            }}
                                        />
                                    </div>
                                    <div style={{ minHeight: 36 }}>
                                        <ErrorMessagesList errors={errors} touched={touched} />
                                        {fileErrors.map((fileError) => (
                                            <HrBotErrorMessage
                                                key={uuidv4()}
                                                label={fileErrorToString("solution", fileError)}
                                            />
                                        ))}
                                    </div>

                                    <ButtonContainer
                                        style={{
                                            marginTop: 18,
                                            justifyContent: THEME.THEME_NAME === "dal" ? "flex-end" : "center",
                                            marginBottom: 32
                                        }}
                                    >
                                        <HrBotButton label={t("screen.codeExample.submit")} type="submit" />
                                    </ButtonContainer>
                                </Form>
                            )}
                        </Formik>
                        <div
                            style={{
                                width: "100%",
                                height: 2,
                                backgroundColor: THEME.customColors.body1Dark,
                                opacity: 0.1,
                                marginBottom: 32
                            }}
                        />
                        <div
                            style={{
                                color: THEME.customColors.body1Dark,
                                opacity: 0.2,
                                marginBottom: 16,
                                fontSize: 14
                            }}
                        >
                            {t(isDal() ? "screen.codeExample.footer.dal" : "screen.codeExample.footer")}
                        </div>
                    </React.Fragment>
                )}
            </ContentWrapper>
        </div>
    );
};
